import React, { useState } from 'react';
import { 
  Plus, Edit, Trash2, Search, Upload, Download, 
  Gift, Calendar, Eye, EyeOff, Save, X, Check,
  Image, Link as LinkIcon, Percent, DollarSign,
  Users, Target, Zap, Star, Tag, Building2, 
  ArrowRight, ChevronDown, MoreHorizontal, AlertCircle
} from 'lucide-react';

interface Campaign {
  id: string;
  title: string;
  description: string;
  type: 'loan' | 'card' | 'deposit' | 'remote_customer';
  bankId?: string;
  bankName?: string;
  bankLogo?: string;
  isActive: boolean;
  isFeatured: boolean;
  startDate: string;
  endDate: string;
  targetAudience: string[];
  discountType: 'percentage' | 'fixed' | 'rate';
  discountValue: string;
  conditions: string[];
  offers?: string[];
  redirectUrl?: string;
  buttonText?: string;
  image?: string;
  clickCount: number;
  conversionRate: string;
  createdAt: string;
  updatedAt: string;
}

interface Bank {
  id: string;
  name: string;
  logo: string;
}

const banks: Bank[] = [
  {
    id: '1',
    name: 'Akbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png'
  },
  {
    id: '2',
    name: 'Garanti BBVA',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png'
  },
  {
    id: '3',
    name: 'Yapı Kredi',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png'
  },
  {
    id: '4',
    name: 'QNB Finansbank',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png'
  },
  {
    id: '5',
    name: 'İş Bankası',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png'
  }
];

const CampaignManagement = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([
    {
      id: '1',
      title: 'Yeni Müşteri Kampanyası',
      description: 'İlk kez kredi kartı alacak müşteriler için özel fırsat',
      type: 'card',
      bankId: '1',
      bankName: 'Akbank',
      bankLogo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
      isActive: true,
      isFeatured: true,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      targetAudience: ['new_customers', 'credit_card'],
      discountType: 'fixed',
      discountValue: '1000',
      conditions: [
        'İlk kez Akbank kredi kartı alacak müşteriler için geçerlidir',
        'Chip-para 3 ay içinde kullanılmalıdır',
        'Nakit çekim ve e-ticarette geçerli değildir'
      ],
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&q=80&w=500&h=300',
      clickCount: 1250,
      conversionRate: '12.5',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-18T10:00:00Z'
    },
    {
      id: '2',
      title: 'Hoş Geldin Kredisi',
      description: '%0 faizli kredi fırsatı',
      type: 'loan',
      bankId: '1',
      bankName: 'Akbank',
      bankLogo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png',
      isActive: true,
      isFeatured: false,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      targetAudience: ['new_customers', 'loan'],
      discountType: 'rate',
      discountValue: '0',
      conditions: [
        'İlk kez Akbank müşterisi olacaklar için geçerlidir',
        '3 ay vade ile kullanılabilir',
        'Kredi notu 1500 ve üzeri olan müşteriler için geçerlidir'
      ],
      clickCount: 890,
      conversionRate: '8.9',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-15T14:30:00Z'
    },
    {
      id: '3',
      title: 'Uzaktan Müşteri Edinimi Kampanyası',
      description: 'Şubeye gitmeden müşteri olun, özel fırsatlardan yararlanın',
      type: 'remote_customer',
      bankId: '2',
      bankName: 'Garanti BBVA',
      bankLogo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png',
      isActive: true,
      isFeatured: true,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      targetAudience: ['new_customers'],
      discountType: 'fixed',
      discountValue: '0',
      conditions: [
        'Yeni müşteriler için geçerlidir',
        'Şubeye gitmeye gerek yok',
        'Kimlik doğrulama gereklidir'
      ],
      offers: [
        '%0 Faizli 50.000 TL\'ye Varan İhtiyaç Kredisi!',
        '%0 Faizli 25.000 TL\'ye varan taksitli nakit avans!'
      ],
      redirectUrl: 'https://example.com/garanti-musteri-ol',
      buttonText: 'Garanti BBVA Müşterisi Ol!',
      clickCount: 1560,
      conversionRate: '14.2',
      createdAt: '2024-01-01T00:00:00Z',
      updatedAt: '2024-01-20T09:15:00Z'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterBank, setFilterBank] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<Partial<Campaign>>({});
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch = campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         campaign.bankName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !filterType || campaign.type === filterType;
    const matchesBank = !filterBank || campaign.bankId === filterBank;
    
    return matchesSearch && matchesType && matchesBank;
  });

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'loan': return <DollarSign size={16} className="text-green-600" />;
      case 'card': return <Gift size={16} className="text-purple-600" />;
      case 'deposit': return <Percent size={16} className="text-blue-600" />;
      case 'remote_customer': return <Users size={16} className="text-orange-600" />;
      default: return <Star size={16} className="text-slate-600" />;
    }
  };

  const getTypeText = (type: string) => {
    switch (type) {
      case 'loan': return 'Kredi';
      case 'card': return 'Kredi Kartı';
      case 'deposit': return 'Mevduat';
      case 'remote_customer': return 'Müşteri Edinimi';
      default: return 'Genel';
    }
  };

  const handleAdd = () => {
    setFormData({
      title: '',
      description: '',
      type: 'remote_customer',
      isActive: true,
      isFeatured: false,
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
      targetAudience: ['new_customers'],
      discountType: 'fixed',
      discountValue: '0',
      conditions: [],
      offers: ['', ''],
      redirectUrl: '',
      buttonText: ''
    });
    setCurrentStep(1);
    setIsModalOpen(true);
    setErrors({});
  };

  const handleEdit = (campaign: Campaign) => {
    setFormData(campaign);
    setIsModalOpen(true);
    setCurrentStep(1);
    setErrors({});
  };

  const handleDelete = (campaignId: string) => {
    if (confirm('Bu kampanyayı silmek istediğinizden emin misiniz?')) {
      setCampaigns(prev => prev.filter(campaign => campaign.id !== campaignId));
    }
  };

  const toggleCampaignStatus = (campaignId: string) => {
    setCampaigns(prev => prev.map(campaign => 
      campaign.id === campaignId 
        ? { ...campaign, isActive: !campaign.isActive, updatedAt: new Date().toISOString() }
        : campaign
    ));
  };

  const toggleFeatured = (campaignId: string) => {
    setCampaigns(prev => prev.map(campaign => 
      campaign.id === campaignId 
        ? { ...campaign, isFeatured: !campaign.isFeatured, updatedAt: new Date().toISOString() }
        : campaign
    ));
  };

  const toggleDropdown = (id: string) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const validateStep = () => {
    const newErrors: Record<string, string> = {};
    
    if (currentStep === 1) {
      if (!formData.type) {
        newErrors.type = 'Kampanya türü seçilmelidir';
      }
      if (!formData.bankId) {
        newErrors.bankId = 'Banka seçilmelidir';
      }
    } else if (currentStep === 2) {
      if (!formData.title?.trim()) {
        newErrors.title = 'Kampanya başlığı gereklidir';
      }
      if (!formData.description?.trim()) {
        newErrors.description = 'Kampanya açıklaması gereklidir';
      }
      if (formData.type === 'remote_customer') {
        if (!formData.offers || formData.offers.length < 2 || !formData.offers[0].trim() || !formData.offers[1].trim()) {
          newErrors.offers = 'En az 2 fırsat metni girilmelidir';
        }
      }
    } else if (currentStep === 3) {
      if (formData.type === 'remote_customer' && !formData.redirectUrl?.trim()) {
        newErrors.redirectUrl = 'Yönlendirme URL\'i gereklidir';
      }
      if (formData.type === 'remote_customer' && !formData.buttonText?.trim()) {
        newErrors.buttonText = 'Buton metni gereklidir';
      }
      if (!formData.startDate) {
        newErrors.startDate = 'Başlangıç tarihi gereklidir';
      }
      if (!formData.endDate) {
        newErrors.endDate = 'Bitiş tarihi gereklidir';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSave = () => {
    if (!validateStep()) return;
    
    if (formData.id) {
      // Update existing campaign
      setCampaigns(prev => prev.map(campaign => 
        campaign.id === formData.id 
          ? { ...campaign, ...formData, updatedAt: new Date().toISOString() }
          : campaign
      ));
    } else {
      // Create new campaign
      const selectedBank = banks.find(bank => bank.id === formData.bankId);
      
      const newCampaign: Campaign = {
        ...formData as Campaign,
        id: Date.now().toString(),
        bankName: selectedBank?.name,
        bankLogo: selectedBank?.logo,
        clickCount: 0,
        conversionRate: '0',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // If it's a remote customer campaign and buttonText is not set, generate default
      if (newCampaign.type === 'remote_customer' && !newCampaign.buttonText && selectedBank) {
        newCampaign.buttonText = `${selectedBank.name} Müşterisi Ol!`;
      }
      
      setCampaigns(prev => [...prev, newCampaign]);
    }
    
    setIsModalOpen(false);
    setFormData({});
    setCurrentStep(1);
  };

  const handleBankChange = (bankId: string) => {
    const selectedBank = banks.find(bank => bank.id === bankId);
    if (selectedBank && formData.type === 'remote_customer') {
      setFormData(prev => ({
        ...prev,
        bankId,
        buttonText: `${selectedBank.name} Müşterisi Ol!`
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        bankId
      }));
    }
  };

  // Function to highlight monetary values with marker effect
  const highlightMonetaryValues = (text: string) => {
    if (!text) return text;
    
    // Regular expression to match monetary values like "25.000 TL" or "1.500TL"
    const regex = /(\d+(?:\.\d+)?)\s*(?:TL|₺|TLL)/g;
    
    // Split the text by monetary values and keep the delimiters
    const parts = [];
    let lastIndex = 0;
    let match;
    
    // Create a new regex object to use with exec
    const regexObj = new RegExp(regex);
    
    // Find all matches and their positions
    while ((match = regexObj.exec(text)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }
      
      // Add the match as a React element
      parts.push(
        <span key={`money-${match.index}`} className="bg-[#F7BA45]/30 px-1 py-0.5 rounded font-semibold">
          {match[0]}
        </span>
      );
      
      // Update lastIndex to continue after this match
      lastIndex = match.index + match[0].length;
    }
    
    // Add any remaining text
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-slate-800">Adım 1: Kampanya Türü ve Banka Seçimi</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Kampanya Türü
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: 'remote_customer' }))}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      formData.type === 'remote_customer'
                        ? 'border-[#F7BA45] bg-[#F7BA45]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Users size={24} className={formData.type === 'remote_customer' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <div className="text-left">
                      <div className="font-medium text-slate-800">Müşteri Edinimi</div>
                      <div className="text-xs text-slate-500">Uzaktan müşteri edinimi kampanyası</div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: 'loan' }))}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      formData.type === 'loan'
                        ? 'border-[#F7BA45] bg-[#F7BA45]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <DollarSign size={24} className={formData.type === 'loan' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <div className="text-left">
                      <div className="font-medium text-slate-800">Kredi</div>
                      <div className="text-xs text-slate-500">Kredi kampanyası</div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: 'card' }))}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      formData.type === 'card'
                        ? 'border-[#F7BA45] bg-[#F7BA45]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Gift size={24} className={formData.type === 'card' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <div className="text-left">
                      <div className="font-medium text-slate-800">Kredi Kartı</div>
                      <div className="text-xs text-slate-500">Kredi kartı kampanyası</div>
                    </div>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: 'deposit' }))}
                    className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                      formData.type === 'deposit'
                        ? 'border-[#F7BA45] bg-[#F7BA45]/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Percent size={24} className={formData.type === 'deposit' ? 'text-[#F7BA45]' : 'text-slate-400'} />
                    <div className="text-left">
                      <div className="font-medium text-slate-800">Mevduat</div>
                      <div className="text-xs text-slate-500">Mevduat kampanyası</div>
                    </div>
                  </button>
                </div>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.type}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Banka Seçin
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {banks.map(bank => (
                    <button
                      key={bank.id}
                      type="button"
                      onClick={() => handleBankChange(bank.id)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                        formData.bankId === bank.id
                          ? 'border-[#F7BA45] bg-[#F7BA45]/5'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <img 
                        src={bank.logo} 
                        alt={bank.name}
                        className="h-10 object-contain"
                      />
                      <div className="text-sm font-medium text-slate-700">{bank.name}</div>
                    </button>
                  ))}
                </div>
                {errors.bankId && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.bankId}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-slate-800">
              Adım 2: Kampanya Detayları
              {formData.type === 'remote_customer' && ' ve Fırsatlar'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Kampanya Başlığı
                </label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                    errors.title ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                  placeholder="Örn: Yeni Müşteri Kampanyası"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.title}
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Kampanya Açıklaması
                </label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                    errors.description ? 'border-red-300 bg-red-50' : 'border-slate-200'
                  }`}
                  placeholder="Kampanya hakkında kısa bir açıklama"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                    <AlertCircle size={14} />
                    {errors.description}
                  </p>
                )}
              </div>
              
              {formData.type === 'remote_customer' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Fırsat Metinleri
                  </label>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Fırsat 1</label>
                      <input
                        type="text"
                        value={formData.offers?.[0] || ''}
                        onChange={(e) => {
                          const newOffers = [...(formData.offers || ['', ''])];
                          newOffers[0] = e.target.value;
                          setFormData(prev => ({ ...prev, offers: newOffers }));
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        placeholder="Örn: %0 Faizli 60.000 TL'ye varan Nakit Fırsatı!"
                      />
                      <div className="mt-2 text-xs text-slate-500">
                        <p>Önizleme:</p>
                        <div className="mt-1 p-2 bg-slate-50 rounded-lg">
                          {formData.offers?.[0] ? highlightMonetaryValues(formData.offers[0]) : ''}
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-slate-500 mb-1">Fırsat 2</label>
                      <input
                        type="text"
                        value={formData.offers?.[1] || ''}
                        onChange={(e) => {
                          const newOffers = [...(formData.offers || ['', ''])];
                          newOffers[1] = e.target.value;
                          setFormData(prev => ({ ...prev, offers: newOffers }));
                        }}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                        placeholder="Örn: Fatura talimatına, ayda 500TL, toplamda 1.500TL'ye varan nakit iade"
                      />
                      <div className="mt-2 text-xs text-slate-500">
                        <p>Önizleme:</p>
                        <div className="mt-1 p-2 bg-slate-50 rounded-lg">
                          {formData.offers?.[1] ? highlightMonetaryValues(formData.offers[1]) : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                  {errors.offers && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.offers}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-slate-800">Adım 3: Yönlendirme ve Yayın Ayarları</h3>
            
            <div className="space-y-4">
              {formData.type === 'remote_customer' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Buton Metni
                    </label>
                    <input
                      type="text"
                      value={formData.buttonText || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, buttonText: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                        errors.buttonText ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                      placeholder="Örn: Akbank Müşterisi Ol!"
                    />
                    {errors.buttonText && (
                      <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {errors.buttonText}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Yönlendirme URL'i
                    </label>
                    <input
                      type="url"
                      value={formData.redirectUrl || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, redirectUrl: e.target.value }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                        errors.redirectUrl ? 'border-red-300 bg-red-50' : 'border-slate-200'
                      }`}
                      placeholder="https://example.com/redirect"
                    />
                    {errors.redirectUrl && (
                      <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle size={14} />
                        {errors.redirectUrl}
                      </p>
                    )}
                  </div>
                </>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Başlangıç Tarihi
                  </label>
                  <input
                    type="date"
                    value={formData.startDate || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                      errors.startDate ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                  {errors.startDate && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.startDate}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Bitiş Tarihi
                  </label>
                  <input
                    type="date"
                    value={formData.endDate || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45] transition-colors ${
                      errors.endDate ? 'border-red-300 bg-red-50' : 'border-slate-200'
                    }`}
                  />
                  {errors.endDate && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle size={14} />
                      {errors.endDate}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                  />
                  <label htmlFor="isActive" className="text-sm text-slate-700">Aktif</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isFeatured"
                    checked={formData.isFeatured || false}
                    onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))}
                    className="rounded border-slate-300 text-[#F7BA45] focus:ring-[#F7BA45]"
                  />
                  <label htmlFor="isFeatured" className="text-sm text-slate-700">Öne Çıkan</label>
                </div>
              </div>
            </div>
            
            {/* Preview Section */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h4 className="font-medium text-slate-800 mb-3">Kampanya Önizleme</h4>
              <div className="bg-white p-4 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  {formData.bankLogo && (
                    <img src={formData.bankLogo} alt={formData.bankName} className="h-8 object-contain" />
                  )}
                  <div>
                    <div className="font-medium text-slate-800">{formData.title || 'Kampanya Başlığı'}</div>
                    <div className="text-sm text-slate-600">{formData.bankName || 'Banka Adı'}</div>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 mb-3">{formData.description || 'Kampanya açıklaması'}</p>
                
                {formData.type === 'remote_customer' && formData.offers && (
                  <div className="space-y-2 mb-3">
                    {formData.offers.map((offer, index) => (
                      <div key={index} className="flex items-start gap-2 text-sm">
                        <Gift size={16} className="text-[#F7BA45] mt-0.5" />
                        <div>{highlightMonetaryValues(offer) || `Fırsat ${index + 1}`}</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {formData.type === 'remote_customer' && (
                  <button className="w-full bg-[#F7BA45] text-slate-900 px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2">
                    {formData.buttonText || 'Müşteri Ol'}
                    <ArrowRight size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Banka Kampanyaları</h1>
              <p className="text-slate-600">Banka kampanyalarını oluşturun ve yönetin</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors">
                <Download size={20} />
                <span>Dışa Aktar</span>
              </button>
              <button 
                onClick={handleAdd}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
              >
                <Plus size={20} />
                <span>Yeni Kampanya</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Kampanya ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Türler</option>
              <option value="loan">Kredi</option>
              <option value="card">Kredi Kartı</option>
              <option value="deposit">Mevduat</option>
              <option value="remote_customer">Müşteri Edinimi</option>
            </select>
            <select
              value={filterBank}
              onChange={(e) => setFilterBank(e.target.value)}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
            >
              <option value="">Tüm Bankalar</option>
              {banks.map(bank => (
                <option key={bank.id} value={bank.id}>{bank.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Campaigns Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Kampanya
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tür
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tarih Aralığı
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İstatistikler
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {campaign.bankLogo && (
                          <img 
                            src={campaign.bankLogo} 
                            alt={campaign.bankName} 
                            className="h-8 w-8 object-contain"
                          />
                        )}
                        <div>
                          <div className="text-sm font-medium text-slate-900">{campaign.title}</div>
                          <div className="text-xs text-slate-500 truncate max-w-xs">{campaign.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(campaign.type)}
                        <span className="text-sm text-slate-700">{getTypeText(campaign.type)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-600">
                        {new Date(campaign.startDate).toLocaleDateString('tr-TR')} - {new Date(campaign.endDate).toLocaleDateString('tr-TR')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-600">
                        <div>Tıklanma: {campaign.clickCount}</div>
                        <div>Dönüşüm: %{campaign.conversionRate}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          campaign.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {campaign.isActive ? <Eye size={12} /> : <EyeOff size={12} />}
                          {campaign.isActive ? 'Aktif' : 'Pasif'}
                        </span>
                        {campaign.isFeatured && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7BA45]/10 text-[#F7BA45] text-xs font-medium">
                            <Star size={12} />
                            Öne Çıkan
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="relative">
                        <button
                          onClick={() => toggleDropdown(campaign.id)}
                          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        >
                          <MoreHorizontal size={18} className="text-slate-500" />
                        </button>
                        
                        {activeDropdown === campaign.id && (
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10">
                            <button
                              onClick={() => {
                                handleEdit(campaign);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              <Edit size={16} className="text-blue-500" />
                              <span>Düzenle</span>
                            </button>
                            <button
                              onClick={() => {
                                toggleCampaignStatus(campaign.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              {campaign.isActive ? (
                                <>
                                  <EyeOff size={16} className="text-slate-500" />
                                  <span>Pasif Yap</span>
                                </>
                              ) : (
                                <>
                                  <Eye size={16} className="text-green-500" />
                                  <span>Aktif Yap</span>
                                </>
                              )}
                            </button>
                            <button
                              onClick={() => {
                                toggleFeatured(campaign.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                            >
                              <Star size={16} className={campaign.isFeatured ? "text-[#F7BA45]" : "text-slate-500"} />
                              <span>{campaign.isFeatured ? 'Öne Çıkarma' : 'Öne Çıkar'}</span>
                            </button>
                            <button
                              onClick={() => {
                                handleDelete(campaign.id);
                                setActiveDropdown(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              <Trash2 size={16} />
                              <span>Sil</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredCampaigns.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-slate-500">
                      <div className="flex flex-col items-center">
                        <Gift size={40} className="text-slate-300 mb-2" />
                        <p>Hiç kampanya bulunamadı</p>
                        <button 
                          onClick={handleAdd}
                          className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors text-sm"
                        >
                          <Plus size={16} />
                          <span>Yeni Kampanya Ekle</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Step-by-Step Campaign Creation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalOpen(false)} />
          <div className="relative min-h-screen flex items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-hidden">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-slate-900">
                    {formData.id ? 'Kampanya Düzenle' : 'Yeni Kampanya Oluştur'}
                  </h2>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Progress Steps */}
              <div className="px-6 pt-6">
                <div className="flex items-center justify-between mb-8">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex flex-col items-center">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        currentStep === step
                          ? 'bg-[#F7BA45] text-white'
                          : currentStep > step
                            ? 'bg-green-500 text-white'
                            : 'bg-slate-200 text-slate-500'
                      }`}>
                        {currentStep > step ? <Check size={20} /> : step}
                      </div>
                      <div className="text-xs mt-2 text-center">
                        {step === 1 && 'Tür ve Banka'}
                        {step === 2 && 'Detaylar'}
                        {step === 3 && 'Yayın Ayarları'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-240px)]">
                {renderStepContent()}
              </div>

              <div className="p-6 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    İptal
                  </button>
                  <div className="flex items-center gap-3">
                    {currentStep > 1 && (
                      <button
                        onClick={handlePrevStep}
                        className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                      >
                        Geri
                      </button>
                    )}
                    {currentStep < 3 ? (
                      <button
                        onClick={handleNextStep}
                        className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                      >
                        İleri
                        <ArrowRight size={20} />
                      </button>
                    ) : (
                      <button
                        onClick={handleSave}
                        className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] text-slate-900 rounded-lg hover:bg-[#e5ac3d] transition-colors"
                      >
                        <Save size={20} />
                        <span>Kaydet</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignManagement;